import React from 'react';
import { ChevronDown, ChevronRight, Layers } from 'lucide-react';

// THE UNIVERSAL API'S ROUTE TREE — modules, the document each URL carries, and the URLs themselves.
//
// ONE DEFINITION, TWO SCREENS. Admin > Payloads maps documents to URLs; Universal API > Redactions shows
// what each document is stripped to. Both are the same tree of the same API, and they had drifted into
// two lists that looked different and were sorted differently, so a reader could not tell whether they
// were seeing the same thing from two angles or two different things.
//
// WHAT THE SCREENS SUPPLY is the content of a row, through render props — the tree itself has no opinion
// about what you do with a payload. That keeps the shape shared and the actions local, rather than a
// component with a `mode` prop growing a branch for every caller.
//
// COLLAPSE STATE LIVES IN THE PARENT. Both screens put an "Expand all" in their own page header, next
// to their own buttons, and a component that owned the state would have to hand it back out again.
//
// TRACKED AS OPEN, NOT AS SHUT, so the default is shut. Fifteen modules and two hundred URLs expanded
// is a page you scroll rather than read, and the module you came for is somewhere in it; shut, the API's
// shape is one screen and one click gets you the part you want. A module imported later is shut like
// every other, which is the same rule rather than an exception.

// Modules, each with the documents under it and the URLs that carry each. Exported because both screens
// need the counts before they render the tree — "15 modules, 3 with no document" is a header line, not
// something to be re-derived from the DOM.
export const groupOperations = (operations) => {
    const byModule = new Map();
    for (const o of operations || []) {
        const key = o.module_path || '(no module)';
        if (!byModule.has(key)) {
            byModule.set(key, { module: key, spec_file: o.spec_file, payloads: new Map(), unmapped: [] });
        }
        const g = byModule.get(key);
        if (!g.spec_file && o.spec_file) g.spec_file = o.spec_file;
        if (o.payload_id) {
            if (!g.payloads.has(o.payload_id)) {
                g.payloads.set(o.payload_id, {
                    payload_id: o.payload_id, payload_name: o.payload_name,
                    schema_bytes: o.schema_bytes, redaction_count: o.redaction_count, urls: [],
                });
            }
            g.payloads.get(o.payload_id).urls.push(o);
        } else {
            g.unmapped.push(o);
        }
    }
    return [...byModule.values()]
        .map(g => ({
            ...g,
            // Most-shared first: a document carrying several URLs is the more consequential row, and it
            // is the one a redaction decision has the widest effect on.
            payloads: [...g.payloads.values()].sort((a, b) =>
                b.urls.length - a.urls.length || String(a.payload_name).localeCompare(b.payload_name)),
        }))
        .sort((a, b) => a.module.localeCompare(b.module));
};

const UapiPayloadTree = ({
    groups,
    expanded,
    onToggleModule,
    renderPayloadActions = null,   // controls on the payload's own line
    renderPayloadExtra = null,     // a block under the payload — the Redactions screen's sets
    renderUnmapped = null,         // a URL with no document; omitted entirely when not supplied
    showUrls = true,
}) => (
    <div className="uapi-groups">
        {groups.map(group => {
            const shut = !expanded.has(group.module);
            return (
                <div key={group.module} className="uapi-group">
                    {/* THE WHOLE HEADER IS THE CONTROL, not just the chevron: a 14px glyph is a target
                        you aim at, and everything on this line means the same thing. */}
                    <div
                        className="uapi-group-head uapi-group-head-clickable"
                        onClick={() => onToggleModule(group.module)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleModule(group.module); }
                        }}
                        aria-expanded={!shut}
                    >
                        {shut
                            ? <ChevronRight size={14} className="uapi-group-chevron" />
                            : <ChevronDown size={14} className="uapi-group-chevron" />}
                        <code className="uapi-group-route">{group.module}</code>
                        {group.spec_file
                            ? <span className="uapi-system-chip">{group.spec_file}</span>
                            : <span className="uapi-override-none">no spec</span>}
                        <span className="lu-muted">
                            {group.payloads.length} payload{group.payloads.length === 1 ? '' : 's'}
                            {renderUnmapped && group.unmapped.length
                                ? ` · ${group.unmapped.length} URL(s) with no payload`
                                : ''}
                        </span>
                    </div>

                    {/* PAYLOAD, then every URL that carries it. The list of URLs under one payload IS the
                        evidence that a document is shared — a count in a chip was missable, a stack of
                        three URLs under one name is not. */}
                    {!shut && group.payloads.map(pl => (
                        <div key={pl.payload_id} className="uapi-pl-row">
                            <div className="uapi-pl-name">
                                <span className="uapi-payload-name">{pl.payload_name}</span>
                                <div className="uapi-payload-meta">
                                    {pl.urls.length > 1 && (
                                        <span className="uapi-shared-chip">
                                            <Layers size={11} /> {pl.urls.length} URLs
                                        </span>
                                    )}
                                    {pl.redaction_count > 0 && (
                                        <span className="uapi-redaction-count">{pl.redaction_count}</span>
                                    )}
                                    <span className="lu-muted">
                                        {Math.max(1, Math.round((pl.schema_bytes || 0) / 1024))} KB
                                    </span>
                                </div>
                                {renderPayloadActions && renderPayloadActions(pl)}
                            </div>
                            {showUrls && (
                                <div className="uapi-pl-urls">
                                    {pl.urls.map(u => (
                                        <div key={u.id} className="uapi-url-line">
                                            <span className={`lu-method lu-method-${String(u.method).toLowerCase()}`}>
                                                {u.method}
                                            </span>
                                            <code className="lu-mono uapi-payload-path">{u.full_path}</code>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {renderPayloadExtra && renderPayloadExtra(pl)}
                        </div>
                    ))}

                    {/* A URL with no document. Only where the screen has something to say about it —
                        Redactions has nothing: there is no body, so there is nothing to strip. */}
                    {!shut && renderUnmapped && group.unmapped.map(o => renderUnmapped(o))}
                </div>
            );
        })}
    </div>
);

export default UapiPayloadTree;
